import { ChatMessage, ChatMilestone } from '../../models/chat';

export type ChatTimelineItem =
  | { kind: 'message'; message: ChatMessage; steps: ChatMilestone[] }
  | { kind: 'steps'; jobId: string; steps: ChatMilestone[] };

export interface ChatTimeline {
  items: ChatTimelineItem[];
  /** The steps of the job still running, shown while it works. */
  running: ChatMilestone[];
}

const jobKey = (jobId: string) => jobId.toLowerCase();

/**
 * Places each job's steps with what the job left in the chat: the running job's steps apart, a finished job's steps
 * on its reply, and the steps of a job that left no reply on their own at the time of their first step.
 */
export function chatTimeline(
  messages: ChatMessage[],
  milestones: ChatMilestone[],
  runningJobId: string | null,
): ChatTimeline {
  const stepsByJob = new Map<string, ChatMilestone[]>();
  for (const step of milestones) {
    const key = jobKey(step.jobId);
    stepsByJob.set(key, [...(stepsByJob.get(key) ?? []), step]);
  }

  const running = runningJobId ? stepsByJob.get(jobKey(runningJobId)) ?? [] : [];
  if (runningJobId) stepsByJob.delete(jobKey(runningJobId));

  const replyOfJob = new Map<string, string>();
  for (const message of messages) {
    if (message.role === 'Assistant' && message.jobId) replyOfJob.set(jobKey(message.jobId), message.id);
  }

  const items: ChatTimelineItem[] = messages.map((message) => {
    const key = message.jobId ? jobKey(message.jobId) : null;
    const isReply = key !== null && replyOfJob.get(key) === message.id;
    return { kind: 'message', message, steps: isReply ? stepsByJob.get(key) ?? [] : [] };
  });

  for (const [key, steps] of stepsByJob) {
    if (replyOfJob.has(key)) continue;
    const startedAt = Date.parse(steps[0].createdAt);
    const before = items.findIndex(
      (item) => item.kind === 'message' && Date.parse(item.message.createdAt) > startedAt,
    );
    items.splice(before === -1 ? items.length : before, 0, { kind: 'steps', jobId: steps[0].jobId, steps });
  }

  return { items, running };
}
